import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { X, Upload, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import api from "@/lib/api";

interface PatientFileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onUploadComplete: () => void;
}

const PatientFileUploadModal = ({
  isOpen,
  onClose,
  organizationId,
  onUploadComplete,
}: PatientFileUploadModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const validFiles = files.filter((file) => {
        const isHTML =
          file.type === "text/html" ||
          file.name.toLowerCase().endsWith(".html");
        const isPDF =
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf");
        const isJSON =
          file.type === "application/json" ||
          file.name.toLowerCase().endsWith(".json");
        return isHTML || isPDF || isJSON;
      });

      if (validFiles.length !== files.length) {
        toast({
          title: "Invalid files",
          description: "Only HTML, PDF, and JSON files are allowed",
          variant: "destructive",
        });
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
    },
    [toast]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const validFiles = files.filter((file) => {
        const isHTML =
          file.type === "text/html" ||
          file.name.toLowerCase().endsWith(".html");
        const isPDF =
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf");
        const isJSON =
          file.type === "application/json" ||
          file.name.toLowerCase().endsWith(".json");
        return isHTML || isPDF || isJSON;
      });

      if (validFiles.length !== files.length) {
        toast({
          title: "Invalid files",
          description: "Only HTML, PDF, and JSON files are allowed",
          variant: "destructive",
        });
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
      e.target.value = "";
    },
    [toast]
  );

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one patient chart file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const response = await api.ingestMultiplePatientFiles(
        organizationId,
        selectedFiles
      );

      toast({
        title: "Upload successful",
        description: response.message,
      });

      setSelectedFiles([]);
      onUploadComplete();
      onClose();
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload patient files. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Patient Charts</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-colors ${
              isDragOver
                ? "border-info bg-info/10"
                : "border-border hover:border-muted-foreground/40"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground/60 mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              Drop patient chart files here or click to browse
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Upload patient chart files (HTML, PDF, JSON) to create patient
              records
            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              multiple
              accept=".html,.pdf,.json,text/html,application/pdf,application/json"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
            <Button
              variant="outline"
              className="cursor-pointer"
              disabled={isUploading}
              onClick={() => document.getElementById("file-upload")?.click()}
              asChild={false}
            >
              Select Files
            </Button>
          </div>

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-muted-foreground/60" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={isUploading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
          >
            {isUploading
              ? "Uploading..."
              : `Upload ${selectedFiles.length} Files`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatientFileUploadModal;
